import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthService,
  GoogleSignInButtonComponent,
  UnauthenticatedForwardNavService,
  UnauthenticatedPageComponent,
} from '@crowdctrl/shared';
import { IonButton, IonIcon, ViewWillEnter } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { arrowForwardOutline } from 'ionicons/icons';
import { environment } from '../../../environments/environment';

addIcons({ arrowForwardOutline });

@Component({
  selector: 'app-login',
  imports: [UnauthenticatedPageComponent, GoogleSignInButtonComponent, IonButton, IonIcon],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements ViewWillEnter {
  readonly unauthNav = inject(UnauthenticatedForwardNavService);

  appName = environment.appName;
  readonly contactEmail = environment.contactEmail?.trim() ?? '';
  isLoading = false;
  activeSignInProvider: 'google' | 'microsoft' | null = null;
  errorMessage: string | null = null;

  ionViewWillEnter(): void {
    this.isLoading = false;
    this.activeSignInProvider = null;
  }

  get contactMailtoHref(): string {
    if (!this.contactEmail) {
      return '';
    }
    const subject = encodeURIComponent(`Interest in CrowdCTRL ${this.appName}`);
    return `mailto:${this.contactEmail}?subject=${subject}`;
  }

  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  async signInWithGoogle(): Promise<void> {
    await this.signIn('google', () => this.authService.signInWithGoogle());
  }

  async signInWithMicrosoft(): Promise<void> {
    await this.signIn('microsoft', () => this.authService.signInWithMicrosoft());
  }

  private async signIn(
    provider: 'google' | 'microsoft',
    signInFn: () => Promise<void>,
  ): Promise<void> {
    this.isLoading = true;
    this.activeSignInProvider = provider;
    this.errorMessage = null;

    try {
      await signInFn();
      const navigated = await this.router.navigate(['/']);
      if (!navigated) {
        this.isLoading = false;
        this.activeSignInProvider = null;
        this.errorMessage = 'Could not open the app. Please try again.';
      }
    } catch (error: unknown) {
      console.error('Login error:', error);

      if (this.authService.isSignInCancelledError(error)) {
        this.isLoading = false;
        this.activeSignInProvider = null;
        return;
      }

      if (this.authService.isUnauthorizedSignInError(error)) {
        const navigated = await this.router.navigate(['/unauthorized']);
        if (!navigated) {
          this.isLoading = false;
          this.activeSignInProvider = null;
          this.errorMessage = 'Could not open the access page. Please try again.';
        }
        return;
      }

      this.isLoading = false;
      this.activeSignInProvider = null;
      this.errorMessage = this.authService.getSignInErrorMessage(error);
    }
  }
}
